/**
 * Add a location: what Bailey and the plan page say about money.
 *
 * Every price here comes from the server's quote (GET /sites/location-quote,
 * docs/add-location-billing.md in beu-node-v2):
 *   { mode, amount, currency, interval, trialEndsAt, pastDue, currentSite,
 *     options: { month: { amount }, year: { amount } } }
 * Amounts are in pounds (59, not 5900), as the server sends them.
 * Nothing in this file knows what the salon plan costs. When the quote can't
 * be read (a 503 while Stripe is unreachable) the words fall back to "Salon
 * plan", never name a figure, and never assume a mode: nothing can be
 * confirmed until the quote loads.
 *
 * Copy rules: no em or en dashes, British spelling, and the price is always
 * shown before anything is committed.
 */

export const MODES = { ADD: 'add_line', TRIAL: 'trial_line', CHECKOUT: 'checkout' };
export const FALLBACK_PLAN = 'Salon plan';
export const QUOTE_FAILED = 'I couldn’t check your plan just now. Try again in a moment.';

const SYMBOLS = { gbp: '£', eur: '€', usd: '$' };

/** "£59", "£599", "£49.50". Empty when there's no number. */
export function money(amount, currency = 'gbp') {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const cur = String(currency || 'gbp').toLowerCase();
  const whole = Number.isInteger(n);
  const num = n.toLocaleString('en-GB', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  return SYMBOLS[cur] ? `${SYMBOLS[cur]}${num}` : `${num} ${cur.toUpperCase()}`;
}

export const normInterval = (i) => (['year', 'yearly', 'annual', 'annually'].includes(String(i || '').toLowerCase()) ? 'year' : 'month');

export const modeOf = (quote) => {
  const m = quote && quote.mode;
  return m === MODES.ADD || m === MODES.TRIAL || m === MODES.CHECKOUT ? m : null;
};

/** The price for an interval: options first (always sent), then the quote's own amount. */
export function amountFor(quote, interval) {
  if (!quote) return null;
  const iv = normInterval(interval || quote.interval);
  const opt = quote.options && quote.options[iv];
  if (opt && opt.amount !== null && opt.amount !== undefined && Number.isFinite(Number(opt.amount))) return Number(opt.amount);
  if (normInterval(quote.interval) === iv && quote.amount !== null && quote.amount !== undefined && Number.isFinite(Number(quote.amount))) return Number(quote.amount);
  return null;
}

/** The interval this owner is billed on: their plan's, or the one they picked when it's a checkout. */
export const intervalOf = (quote, picked) => (modeOf(quote) === MODES.CHECKOUT && picked ? normInterval(picked) : normInterval(quote && quote.interval));

/** "£59 a month" */
export function priceWords(quote, interval) {
  const iv = normInterval(interval || (quote && quote.interval));
  const a = amountFor(quote, iv);
  return a === null ? null : `${money(a, quote.currency)} a ${iv}`;
}

/** "£59/month" */
export function priceSlash(quote, interval) {
  const iv = normInterval(interval || (quote && quote.interval));
  const a = amountFor(quote, iv);
  return a === null ? null : `${money(a, quote.currency)}/${iv}`;
}

/** Twelve months against a year, when a year is cheaper. */
export function yearlySaving(quote) {
  const m = amountFor(quote, 'month');
  const y = amountFor(quote, 'year');
  if (m === null || y === null) return null;
  const s = Math.round((m * 12 - y) * 100) / 100;
  return s > 0 ? s : null;
}

export function toDate(v) {
  if (v === null || v === undefined || v === '') return null;
  // Stripe sends seconds; the server may pass them straight on.
  const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** "14 Oct" */
export function shortDate(v) {
  const d = toDate(v);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' }) : '';
}

/** "3 September 2026" */
export function longDate(v) {
  const d = toDate(v);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' }) : '';
}

export function siteNameOf(site) {
  if (!site) return '';
  if (typeof site === 'string') return site.trim();
  return String(site.salonName || site.name || '').trim();
}

/**
 * Bailey's opening line for a new location, per billing mode.
 *   add_line    Opening somewhere new? Another location is £59 a month, the same plan as Tolworth.
 *   trial_line  Opening somewhere new? Covered by your trial. £59 a month from 14 Oct.
 *   checkout    Opening somewhere new? Your new location gets its own plan: £59 a month or £599 a year.
 */
export function introLine(quote, { site } = {}) {
  const mode = modeOf(quote);
  const from = siteNameOf(site) || siteNameOf(quote && quote.currentSite) || 'your salon';
  if (!mode || amountFor(quote, mode === MODES.CHECKOUT ? 'month' : quote.interval) === null) {
    return 'Opening somewhere new? Each location goes on the Beu salon plan, and I’ll show you the price before anything is charged.';
  }
  if (mode === MODES.CHECKOUT) {
    const m = priceWords(quote, 'month');
    const y = priceWords(quote, 'year');
    return `Opening somewhere new? Your new location gets its own plan: ${[m, y].filter(Boolean).join(' or ')}.`;
  }
  const words = priceWords(quote);
  if (mode === MODES.TRIAL) {
    const when = shortDate(quote.trialEndsAt);
    return when
      ? `Opening somewhere new? Covered by your trial. ${capitalise(words)} from ${when}.`
      : `Opening somewhere new? Covered by your trial, then ${words}.`;
  }
  return `Opening somewhere new? Another location is ${words}, the same plan as ${from}.`;
}

/** The price, in one small card under the name and address: seen before any commitment. */
export function priceSummary(quote) {
  const mode = modeOf(quote);
  if (!mode || (mode !== MODES.CHECKOUT && amountFor(quote) === null) || (mode === MODES.CHECKOUT && amountFor(quote, 'month') === null && amountFor(quote, 'year') === null)) {
    return { price: FALLBACK_PLAN, detail: 'I’ll show you the price before anything is charged.' };
  }
  if (mode === MODES.CHECKOUT) {
    return {
      price: [priceSlash(quote, 'month'), priceSlash(quote, 'year')].filter(Boolean).join(' or '),
      detail: 'Its own plan, paid by card when you confirm. Your current plan stays as it is.',
    };
  }
  if (mode === MODES.TRIAL) {
    const when = shortDate(quote.trialEndsAt);
    return { price: priceSlash(quote), detail: when ? `Covered by your trial. ${priceSlash(quote)} from ${when}.` : 'Covered by your trial for now.' };
  }
  return { price: priceSlash(quote), detail: 'Pro rata from the day you confirm, on your existing invoice.' };
}

/**
 * The plan page's card for one mode. `interval` is only used for a checkout.
 *   { mode, title, amount, per, detail, button, note: { tone, text } | null, canConfirm }
 */
export function planCard(quote, { name, interval } = {}) {
  const who = String(name || '').trim() || 'this location';
  const title = `${who} · Beu salon plan`;
  const mode = modeOf(quote);
  const iv = intervalOf(quote, interval);
  const a = amountFor(quote, iv);
  if (!mode || a === null) {
    return {
      mode: null,
      title,
      amount: FALLBACK_PLAN,
      per: '',
      detail: QUOTE_FAILED,
      button: 'Try again',
      note: null,
      canConfirm: false,
    };
  }
  const amount = money(a, quote.currency);
  const per = `/${iv}`;
  if (mode === MODES.CHECKOUT) {
    const saving = yearlySaving(quote);
    return {
      mode,
      title,
      amount,
      per,
      detail: iv === 'year'
        ? `${amount} billed yearly.${saving ? ` Save ${money(saving, quote.currency)} on paying monthly.` : ''}`
        : 'Billed monthly. Cancel any time.',
      button: 'Continue to payment',
      note: { tone: 'info', text: `${who} gets its own plan, separate from your current one. You pay on the next page, with Stripe.` },
      canConfirm: true,
    };
  }
  const slash = `${amount}/${iv}`;
  const card = {
    mode,
    title,
    amount,
    per,
    detail: mode === MODES.TRIAL
      ? (shortDate(quote.trialEndsAt) ? `Covered by your trial. ${slash} from ${shortDate(quote.trialEndsAt)}.` : `Covered by your trial, then ${slash}.`)
      : `${slash}, pro rata from today, on your existing invoice.`,
    button: `Add ${who} to my plan`,
    note: null,
    canConfirm: true,
  };
  if (quote.pastDue) {
    card.note = { tone: 'warn', text: 'Your last payment needs sorting first. Update your card in Billing and Stripe will try again, with this location on the same invoice.' };
  }
  return card;
}

/** What Bailey says above the plan page. */
export function planLine(quote, { name, interval } = {}) {
  const who = String(name || '').trim() || 'This location';
  const mode = modeOf(quote);
  const iv = intervalOf(quote, interval);
  if (!mode || amountFor(quote, iv) === null) return `${QUOTE_FAILED} Nothing is added until you can see the price.`;
  if (mode === MODES.CHECKOUT) return `${who} gets its own plan. Pick monthly or yearly, then pay securely with Stripe.`;
  if (mode === MODES.TRIAL) {
    const when = shortDate(quote.trialEndsAt);
    return when ? `${who} is covered by your trial. The first charge for it is on ${when}.` : `${who} is covered by your trial for now.`;
  }
  if (quote.pastDue) return `${who} goes on your plan at ${priceSlash(quote)}. Your last payment needs sorting too.`;
  return `${who} goes on your plan at ${priceSlash(quote)}, pro rata from today.`;
}

/** The toggle's labels for a checkout: "Monthly £59", "Yearly £599", and "Save £109". */
export function checkoutOptions(quote) {
  const saving = yearlySaving(quote);
  return {
    month: amountFor(quote, 'month') === null ? null : money(amountFor(quote, 'month'), quote.currency),
    year: amountFor(quote, 'year') === null ? null : money(amountFor(quote, 'year'), quote.currency),
    save: saving ? `Save ${money(saving, quote.currency)}` : null,
  };
}

/* ---------------- where to pick up ---------------- */

export const TRACK_STEPS = ['newLocation', 'bringOver', 'plan'];

/** The site an intent bills instead of creating one ("Add to plan" on Billing). */
export const existingSalonOf = (intent) => {
  if (!intent) return null;
  const id = intent.existingSalonId || (intent.answers && intent.answers.existingSalonId) || null;
  return id ? String(id) : null;
};

/**
 * Where the flow picks up for the open intent (GET location-intents/current).
 *   newLocation | bringOver | plan | building | created
 * `requested` is a step asked for by URL (Back from the plan page); it's
 * honoured only while the intent is still a draft and the step makes sense.
 */
export function resumeStep(intent, requested) {
  if (!intent || intent.status === 'cancelled') return 'newLocation';
  if (intent.status === 'created') return 'created';
  if (intent.status === 'billing_confirmed') return 'building';
  if (intent.status === 'awaiting_payment') return 'plan';
  if (existingSalonOf(intent)) return 'plan';
  const a = intent.answers || {};
  // Named AND addressed: the site can't be built without an address
  const named = String(a.salonName || '').trim().length > 1 && !!String(a.formattedAddress || '').trim();
  if (!named) return 'newLocation';
  if (requested === 'newLocation' || requested === 'bringOver') return requested;
  if (TRACK_STEPS.includes(intent.step)) return intent.step;
  return a.copy ? 'plan' : 'bringOver';
}

/* ---------------- Billing: one line per location ---------------- */

/**
 * The line under a location on Billing, from /sites/mine's per-site
 * isBaseSite and billing { subscriptionItemId, addedAt, mode, interval,
 * intentId, subscriptionStatus }.
 *   base      the group's first site: the owner's own plan pays for it
 *   line      on the owner's plan: "£59/month · added 3 September 2026"
 *   trial     on the plan, inside the trial: "Covered by trial until 14 Oct"
 *   own       its own subscription (checkout mode): "Own plan · £59/month"
 *   unbilled  a non-base site with no line: "Not on your plan yet"
 */
const isBase = (site, baseSalonId) => {
  if (!site) return false;
  if (site.isBaseSite === true) return true;
  if (site.isBaseSite === false) return false;
  return !!baseSalonId && String(site.salonId) === String(baseSalonId);
};
/** A location whose own plan stopped paying (cancelled, unpaid or expired). */
export const isLapsed = (site) => !!(site && site.billing && site.billing.lapsed === true);
export const lapsedLine = (name) => `The plan for ${String(name || '').trim() || 'this location'} has lapsed. Bookings carry on as normal, but please sort the payment.`;

export function billingRow(site, { quote, baseSalonId } = {}) {
  const b = (site && site.billing) || null;
  if (isLapsed(site)) return { kind: 'lapsed', text: 'Plan lapsed, payment needed', canSort: true };
  if (b && b.subscriptionItemId) {
    const iv = normInterval(b.interval || (quote && quote.interval));
    const price = quote ? priceSlash(quote, iv) : null;
    const owing = ['past_due', 'unpaid'].includes(b.subscriptionStatus) ? 'last payment needs sorting' : '';
    if (b.mode === MODES.CHECKOUT) return { kind: 'own', text: ['Own plan', price, owing].filter(Boolean).join(' · ') };
    const trialing = b.subscriptionStatus ? b.subscriptionStatus === 'trialing' : b.mode === MODES.TRIAL;
    if (trialing) {
      const until = modeOf(quote) === MODES.TRIAL ? shortDate(quote.trialEndsAt) : '';
      return { kind: 'trial', text: until ? `Covered by trial until ${until}${price ? `, then ${price}` : ''}` : 'Covered by your trial' };
    }
    return { kind: 'line', text: [price || FALLBACK_PLAN, b.addedAt ? `added ${longDate(b.addedAt)}` : '', owing].filter(Boolean).join(' · ') };
  }
  if (isBase(site, baseSalonId)) return { kind: 'base', text: 'Base plan' };
  // A linked location whose own account holder already pays Beu for it.
  if (site && site.paidByOwnAccount) return { kind: 'own-account', text: 'On its own account’s plan' };
  return { kind: 'unbilled', text: 'Not on your plan yet', canAdd: true };
}

/** The group's first site is the one the owner's own plan pays for. */
export function baseSalonIdOf(group, sites) {
  const flagged = (sites || []).find((s) => s && s.isBaseSite === true);
  if (flagged) return String(flagged.salonId);
  const ids = (group && Array.isArray(group.salonIds) && group.salonIds) || [];
  if (ids.length) return String(ids[0] && ids[0]._id ? ids[0]._id : ids[0]);
  const first = (sites || [])[0];
  return first ? String(first.salonId) : null;
}

/* ---------------- after billing ---------------- */

export const buildingLine = (name) => `Setting up ${String(name || '').trim() || 'your new location'}…`;
export const createdLine = (name) => `${String(name || '').trim() || 'Your new location'} is on your plan. Now let’s get it ready to take bookings.`;

/** The server's error code (402, 403, 409, 503…), when there is one. */
export function errorCodeOf(e) {
  const info = (e && e.info) || (e && e.response && e.response.data && e.response.data.error) || null;
  const code = (e && e.code) || (info && info.errorCode) || (e && e.response && e.response.status) || null;
  if (code === null || code === undefined || code === '') return null;
  const n = Number(code);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A server or Stripe message, as plain as it came. */
export function errorText(e, fallback = 'That didn’t work just now. Please try again.') {
  const d = e && e.response && e.response.data;
  const msg = (d && (d.message || (d.error && (d.error.responseMessage || d.error.message)) || (typeof d.error === 'string' ? d.error : null)))
    || (e && e.message) || '';
  return String(msg || fallback).replace(/\s*[–—]\s*/g, ', ');
}

function capitalise(s) {
  const t = String(s || '');
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}
